/**
 * 人事变动前核对：汇总员工身份、候选业务和资产，避免把公司号码的继任者当成同一员工。
 * 清单只读取资料；经理设置会单独保存，姓名可能重名，候选记录须经人事核对。
 */
import React, { useEffect, useRef, useState } from "react";
import { AlertTriangle, Loader2 } from "lucide-react";
import { useApp } from "../../context/AppContext";
import { toast } from "../ui/sonner";

const LABELS = {
  projects: ["工程项目", "Construction projects"],
  designProjects: ["设计项目", "Design projects"],
  leads: ["客户线索", "Customer leads"],
  recycledLeads: ["回收站线索", "Recycled leads"],
  referrals: ["转介绍", "Referrals"],
  aftersales: ["售后工单", "After-sales cases"],
  accounts: ["公司号码 / 邮箱台账", "Company phone / email records"],
  assets: ["领用设备", "Assigned equipment"],
};

const ROLE_LABELS = {
  manager: ["场地 PM", "On-site PM"], onlineManager: ["线上 PM", "Online PM"],
  designer: ["建筑设计", "Designer"], drafter: ["施工图", "Drafter"],
  sales: ["销售", "Sales"], cs: ["客服", "Customer service"],
};

function itemLabel(key, item) {
  if (key === "accounts") return item.value || item.id;
  if (key === "assets") return item.name || item.id;
  return item.clientName || item.name || item.title || item.address || item.projectId || item.refNo || item.id;
}

export function TransitionPreview({ employeeId, transitionType, zh }) {
  const { api } = useApp();
  const T = (cn, en) => (zh ? cn : en);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [retry, setRetry] = useState(0);
  const [loadingGroup, setLoadingGroup] = useState("");
  const [branches, setBranches] = useState([]);
  const [managerEdit, setManagerEdit] = useState(false);
  const [managerChoice, setManagerChoice] = useState("");
  const [managerBusy, setManagerBusy] = useState(false);
  const [managerError, setManagerError] = useState("");
  const selectedEmployee = useRef(employeeId);
  selectedEmployee.current = employeeId;

  useEffect(() => () => { selectedEmployee.current = null; }, []);

  useEffect(() => {
    let alive = true;
    api.get("/branches").then(({ data: rows }) => { if (alive) setBranches(rows || []); }).catch(() => {});
    return () => { alive = false; };
  }, [api]);

  useEffect(() => {
    let alive = true;
    setData(null);
    setError("");
    setLoadingGroup("");
    setManagerEdit(false);
    setManagerChoice("");
    setManagerError("");
    if (employeeId) {
      api.get(`/hrms/employees/${encodeURIComponent(employeeId)}/transition-preview`)
        .then(({ data: result }) => { if (alive) setData(result); })
        .catch((err) => { if (alive) setError(err?.response?.data?.detail || (zh ? "清单加载失败" : "Could not load the checklist")); });
    }
    return () => { alive = false; };
  }, [api, employeeId, retry, zh]);

  const loadMore = async (key) => {
    const next = data?.groups?.[key]?.nextOffset;
    if (next == null || loadingGroup) return;
    setLoadingGroup(key);
    try {
      const result = await api.get(`/hrms/employees/${encodeURIComponent(employeeId)}/transition-preview`, { params: { category: key, offset: next } });
      setData((prev) => {
        if (!prev || prev.employee.id !== employeeId) return prev;
        const current = prev.groups[key];
        const page = result.data.groups[key];
        return { ...prev, groups: { ...prev.groups, [key]: { ...page, items: [...current.items, ...page.items] } } };
      });
    } catch (err) {
      if (selectedEmployee.current === employeeId) setError(err?.response?.data?.detail || T("加载更多失败，请重试", "Could not load more; please retry"));
    } finally {
      if (selectedEmployee.current === employeeId) setLoadingGroup("");
    }
  };

  // 经理配置按原分公司和部门保存，与客户归属及历史业绩分开；版本冲突要求先刷新。
  const saveManager = async () => {
    if (!managerChoice || !data?.employee?.branch || !data?.employee?.department) return;
    setManagerBusy(true);
    setManagerError("");
    try {
      const emp = data.employee;
      const { data: saved } = await api.put(`/hrms/handover-manager/${encodeURIComponent(emp.branch)}/${encodeURIComponent(emp.department)}`,
        { employeeId: managerChoice, version: data.manager.version });
      if (!saved.auditLogged) toast.warning(T("经理已保存，审计中心暂不可用；变更记录已保留。", "Manager saved. The audit center is unavailable; the change history was retained."));
      if (selectedEmployee.current === employeeId) setRetry((n) => n + 1);
    } catch (err) {
      if (selectedEmployee.current === employeeId) setManagerError(err?.response?.data?.detail || T("设置失败，请重试", "Could not save; please retry"));
    } finally {
      if (selectedEmployee.current === employeeId) setManagerBusy(false);
    }
  };

  if (!employeeId) return null;
  if (error) return (
    <div role="alert" className="rounded-lg border border-destructive/40 bg-destructive/5 px-3 py-2 text-xs text-destructive">
      {error}{" "}<button type="button" onClick={() => setRetry((n) => n + 1)} className="underline focus-visible:ring-2 focus-visible:ring-brand-400">{T("重新加载", "Reload")}</button>
    </div>
  );
  if (!data || data.employee.id !== employeeId) return <p className="text-xs text-muted-foreground" role="status"><Loader2 className="mr-1 inline h-3.5 w-3.5 animate-spin" />{T("正在整理关联清单", "Loading related records")}</p>;

  const emp = data.employee;
  const branchName = (id) => {
    const branch = branches.find((b) => b.id === id);
    return branch ? (zh ? branch.zh : branch.en || branch.zh) : id;
  };
  const branchLabel = branchName(emp.branch);
  const department = (zh ? emp.departmentName : emp.departmentNameEn) || emp.department;
  const roleName = (key) => (ROLE_LABELS[key] || [key, key])[zh ? 0 : 1];

  return (
    <section className="space-y-2 rounded-lg border border-border bg-muted/30 p-3" aria-label={T("变动前关联清单", "Related records before personnel change")} data-testid="hrms-transition-preview">
      <h3 className="text-sm font-semibold text-foreground">{T("变动前关联清单", "Related records")}</h3>
      <p className="text-xs text-muted-foreground">
        {[emp.name, branchLabel, department].filter(Boolean).join(" · ")}
        {data.linkedAccount ? ` · ${T("账号", "Account")}: ${data.linkedAccount.username} (${data.linkedAccount.disabled ? T("已停用", "disabled") : data.linkedAccount.accessAllowed === false ? T("登录已失效", "access stopped") : T("可登录", "access active")})` : ` · ${T("未关联登录账号", "No linked login account")}`}
      </p>
      {emp.profilePhone && <p className="text-xs text-muted-foreground">{T("员工档案电话", "Phone in employee record")}: {emp.profilePhone}</p>}
      {transitionType !== "onboard" && <div className="rounded-md border border-border bg-background px-2 py-2 text-xs">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-medium text-foreground">{T("原部门交接经理", "Original department handover manager")}</span>
          <span className="text-muted-foreground">{data.manager?.status === "available" ? `${data.manager.name} (${data.manager.username})`
            : data.manager?.status === "departing" ? T("当前经理是变动员工，请另行指定", "The configured manager is this employee; choose another")
            : data.manager?.status === "unavailable" ? T("已配置的经理不可用，请重新指定", "The configured manager is unavailable")
            : T("尚未设置", "Not configured")}</span>
          {data.managerCanConfigure && !managerEdit && <button type="button" className="ml-auto text-brand-600 underline focus-visible:ring-2 focus-visible:ring-brand-400" onClick={() => { setManagerChoice(data.manager?.employeeId || ""); setManagerEdit(true); }}>
            {data.manager?.status === "available" ? T("更换", "Change") : T("设置", "Configure")}
          </button>}
        </div>
        <p className="mt-1 text-muted-foreground">{T("这里只配置默认接收人；客户和在建工程项目仍需在人事变动的业务清单中确认并交接。", "This sets the default recipient. Customers and active construction projects still need confirmation and handover in the personnel change checklist.")}</p>
        {managerEdit && (
          <div className="mt-2 space-y-2">
            <label className="block text-muted-foreground">{T("选择本部门在职员工作为交接经理", "Select an active employee in this department as handover manager")}
              <select value={managerChoice} onChange={(ev) => setManagerChoice(ev.target.value)} className="mt-1 w-full rounded-md border border-input bg-background px-2 py-1.5 text-foreground focus-visible:ring-2 focus-visible:ring-brand-400">
                <option value="">{T("请选择", "Select")}</option>
                {(data.managerCandidates || []).map((candidate) => <option key={candidate.employeeId} value={candidate.employeeId}>{candidate.name}{(zh ? candidate.positionName : candidate.positionNameEn) ? ` · ${zh ? candidate.positionName : candidate.positionNameEn}` : ""} ({candidate.username})</option>)}
              </select>
            </label>
            {!(data.managerCandidates || []).length && <p className="text-muted-foreground">{T("该部门暂无已关联启用账号的在职员工，请先核对员工档案和账号。", "No active employees with an enabled account are available in this department.")}</p>}
            {managerError && <p className="text-destructive" role="alert">{managerError}</p>}
            <div className="flex gap-2">
              <button type="button" disabled={managerBusy || !managerChoice} onClick={saveManager} className="rounded-md bg-primary px-3 py-1.5 text-primary-foreground disabled:opacity-50 focus-visible:ring-2 focus-visible:ring-brand-400">{managerBusy ? T("保存中", "Saving") : T("保存经理", "Save manager")}</button>
              <button type="button" disabled={managerBusy} onClick={() => { setManagerEdit(false); setManagerError(""); }} className="rounded-md border border-border px-3 py-1.5 focus-visible:ring-2 focus-visible:ring-brand-400">{T("取消", "Cancel")}</button>
            </div>
          </div>
        )}
        {!data.managerCanConfigure && <p className="mt-1 text-muted-foreground">{T("请先核对员工的分公司和部门设置。", "Check the employee's branch and department settings first.")}</p>}
      </div>}
      <p className="text-xs text-muted-foreground">{T("项目与客户是系统查到的候选记录，未匹配到的记录仍需核对。请确认员工档案电话是否为公司号码，以及是否已登记台账。", "Projects and customers are possible matches; unmatched records still need review. Confirm whether the employee phone is a company number and whether it is recorded in the ledger.")}</p>
      {(data.phoneDuplicates?.active || data.phoneDuplicates?.left) ? (
        <p className="flex items-start gap-1.5 rounded-md border border-amber-300 bg-amber-50 px-2 py-1.5 text-xs text-amber-900" role="alert">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          {T(`其他员工档案也有此电话（未标记离职 ${data.phoneDuplicates.active} 人、已标记离职 ${data.phoneDuplicates.left} 人）。请核对是否为公司号码继承，不要据此合并员工。`, `This phone appears on other employee records (${data.phoneDuplicates.active} not marked departed, ${data.phoneDuplicates.left} marked departed). Check whether it is a reused company number; do not merge the employees.`)}
        </p>
      ) : null}
      {(data.ambiguousNames || []).length > 0 && (
        <p className="flex items-start gap-1.5 rounded-md border border-amber-300 bg-amber-50 px-2 py-1.5 text-xs text-amber-900" role="alert">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          {T(`名称 ${data.ambiguousNames.join("、")} 同时出现在其他员工档案中，请逐项核对，不能按名称自动认定归属。`, `The name(s) ${data.ambiguousNames.join(", ")} also appear on other employee records. Review each match before assigning it.`)}
        </p>
      )}
      <div className="divide-y divide-border rounded-md border border-border bg-background">
        {Object.entries(LABELS).map(([key, labels]) => {
          const group = data.groups[key];
          if (!group) return null;
          return (
            <details key={key} className="px-2 py-1.5" data-testid={`hrms-transition-preview-${key}`}>
              <summary className="cursor-pointer text-xs font-medium text-foreground focus-visible:ring-2 focus-visible:ring-brand-400">{labels[zh ? 0 : 1]} · {group.total}</summary>
              {group.items.length === 0 ? <p className="py-2 text-xs text-muted-foreground">{T("未查到候选记录", "No matching records found")}</p> : (
                <ul className="mt-1.5 space-y-1">
                  {group.items.map((item, idx) => {
                    const roles = [...(item.roleFields || []).map(roleName), ...(item.coRoleFields || []).map((r) => `${T("协办", "Support")}: ${roleName(r)}`)];
                    return <li key={`${item.id || "item"}-${idx}`} className="break-words text-xs text-muted-foreground">
                      <span className="font-medium text-foreground">{itemLabel(key, item)}</span>
                      {item.address && item.address !== itemLabel(key, item) ? ` · ${item.address}` : ""}
                      {(item.branch || item.region) ? ` · ${branchName(item.branch || item.region)}` : ""}
                      {roles.length ? ` · ${roles.join(" / ")}` : ""}
                      {item.id && item.id !== itemLabel(key, item) ? ` · ${item.id}` : ""}
                    </li>;
                  })}
                </ul>
              )}
              {group.nextOffset != null && <button type="button" disabled={!!loadingGroup} onClick={() => loadMore(key)} className="mt-2 text-xs text-brand-600 underline disabled:opacity-50 focus-visible:ring-2 focus-visible:ring-brand-400">
                {loadingGroup === key ? T("加载中", "Loading") : T(`加载更多（已显示 ${group.items.length} / ${group.total}）`, `Load more (${group.items.length} / ${group.total})`)}
              </button>}
            </details>
          );
        })}
      </div>
    </section>
  );
}
